import React, { useEffect, useContext, useState } from 'react'
import { Form, Modal, Row, Switch, Col, Input, Button, Tooltip, AutoComplete } from 'antd'
import { HistoryOutlined, EditOutlined } from '@ant-design/icons'
import _ from 'lodash'
import FormLayout from 'components/FormLayout'
import ManageSuppliers, { ManageSuppliersContext } from 'components/ManageSuppliers'

export default function InsuranceSuppliers() {
    return (
        <ManageSuppliers product="insurance" resource="insurance-supplier">
            <InternalInsuranceSuppliers />
        </ManageSuppliers>
    )
}

function InternalInsuranceSuppliers() {
    const { canEdit, onRegisterColumns, onOpenEditForm, onSelectSupplier, dispatchEvent } = useContext(
        ManageSuppliersContext
    )

    const onShowChangeStatusConfirm = record => {
        Modal.confirm({
            maskClosable: true,
            title: `Are you sure to turn ${record.active ? 'off' : 'on'} ${record.name}`,
            onOk: async () => {
                dispatchEvent('status', record)
            }
        })
    }

    const columns = [
        {
            key: 'code',
            dataIndex: 'code',
            title: 'Code',
            defaultSortOder: 'ascend'
        },
        {
            key: 'name',
            dataIndex: 'name',
            title: 'Name',
            width: '20%',
            ellipsis: true
        },
        {
            key: 'status',
            dataIndex: 'active',
            title: 'Active',
            render: (_, record) => (
                <Switch disabled={!canEdit} checked={record.active} onClick={() => onShowChangeStatusConfirm(record)} />
            )
        },
        {
            key: 'action',
            title: 'Action',
            render: (_, record) => {
                return (
                    <Row gutter={[8, 8]}>
                        <Col sm={24} md={8}>
                            <Tooltip title="Edit">
                                <Button
                                    ghost
                                    type="primary"
                                    icon={<EditOutlined />}
                                    disabled={!canEdit}
                                    onClick={() => onOpenEditForm(record)}
                                />
                            </Tooltip>
                        </Col>
                        <Col sm={24} md={8}>
                            <Tooltip title="Show Log">
                                <Button
                                    type="dashed"
                                    icon={<HistoryOutlined />}
                                    onClick={() => onSelectSupplier(record)}
                                />
                            </Tooltip>
                        </Col>
                    </Row>
                )
            }
        }
    ]

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => onRegisterColumns(columns), [])

    return (
        <React.Fragment>
            <InsuranceSupplierForm />
        </React.Fragment>
    )
}

function InsuranceSupplierForm() {
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const { suppliers, formState, onHideForm, onSave, onReload } = useContext(ManageSuppliersContext)

    const fields = ['code', 'name', 'username', 'passwd', 'email', 'url', 'default_currency']

    useEffect(() => {
        form.resetFields()

        if (formState.action === 'edit') {
            const fieldValues = _.pick(formState.payload, fields)
            form.setFieldsValue(fieldValues)
        }
    }, [formState])

    const onSubmit = () => {
        form.validateFields().then(fieldsValue => {
            setLoading(true)

            onSave(fieldsValue)
                .then(() => {
                    setLoading(false)
                    onHideForm()
                    onReload()
                })
                .catch(() => {
                    setLoading(false)
                })
        })
    }

    const onPaste = () => {}

    return (
        <FormLayout
            type="supplier"
            action={formState.action}
            open={formState.action === 'create' || formState.action === 'edit'}
            loading={loading}
            onCloseForm={onHideForm}
            onSubmit={onSubmit}
            onPaste={onPaste}>
            <Form form={form} colon={false} layout="vertical">
                <Row gutter={16}>
                    <Col md={12}>
                        <Form.Item
                            label="Code"
                            name="code"
                            validateTrigger="onBlur"
                            hasFeedback
                            required
                            rules={[
                                {
                                    required: true,
                                    message: 'Supplier code is required'
                                }
                            ]}>
                            <AutoComplete
                                showSearch
                                disabled={formState.action === 'edit'}
                                filterOption={(input, option) => RegExp('.*' + input, 'gi').test(option.label)}
                                options={_.chain(suppliers)
                                    .unionBy(s => s.code)
                                    .map(s => ({
                                        label: s.code,
                                        value: s.code
                                    }))
                                    .value()}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col md={12}>
                        <Form.Item label="Username" name="username" hasFeedback>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col md={12}>
                        <Form.Item label="Password" name="passwd" hasFeedback>
                            <Input.Password />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col md={12}>
                        <Form.Item
                            label="Name"
                            name="name"
                            validateTrigger="onBlur"
                            hasFeedback
                            required
                            rules={[
                                {
                                    required: true,
                                    message: 'Name is required'
                                }
                            ]}>
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col md={12}>
                        <Form.Item label="Email" name="email">
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col md={24}>
                        <Form.Item
                            label="Url"
                            name="url"
                            validateTrigger="onBlur"
                            hasFeedback
                            required
                            rules={[
                                {
                                    required: true,
                                    message: 'URL is required'
                                }
                            ]}>
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col md={12}>
                        <Form.Item
                            label="Default Currency"
                            name="default_currency"
                            hasFeedback
                            rules={[
                                {
                                    max: 3,
                                    message: 'Currency code must be 3 alphabet characters'
                                }
                            ]}>
                            <Input maxLength={3} />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </FormLayout>
    )
}
